"use client";

import { useQuery } from "@tanstack/react-query";
import { thetaApi } from "@/lib/api/theta";
import { retryUnlessRefused } from "@/lib/hooks/queryRetry";

/**
 * "May I write to this person, and does the thread already exist?" —
 * `GET /users/{id}/messaging`, as one query under one key.
 *
 * Two surfaces ask it: the profile's "Send besked" button, which needs to know
 * WHERE the button goes (`useSendMessageById`), and `/beskeder/ny`, which
 * offers the existing thread instead of a composer once a single recipient is
 * picked. It sits here rather than beside its first caller so the second one
 * does not have to import the ChatDock to ask a question about a user.
 *
 * `userId: null` is "nobody is chosen yet". `enabled` is how a caller that
 * already holds the verdict — `UserProfile.can.message`, which is the same one
 * this route carries — keeps the request unsent.
 */
/**
 * The prefix every entry lives under.
 *
 * EXPORTED SO IT CAN BE INVALIDATED. This answer goes stale the moment a 1:1
 * is created, accepted, declined or left, and it is cached for a minute — so
 * without an invalidation, sending a first message and returning to "Ny
 * besked" within that minute served the pre-send `conversationId: null` and
 * offered to start the thread you had just started. Every write that can move
 * a 1:1 in or out of existence invalidates this whole prefix rather than one
 * user's entry, because the conversation-scoped writes know a conversation id
 * and not the other person's user id.
 */
export const userMessagingKey = ["v1", "user-messaging"] as const;

export function useUserMessaging(userId: number | null, enabled = true) {
  return useQuery({
    queryKey: [...userMessagingKey, userId],
    queryFn: () => thetaApi.userMessaging(userId!),
    enabled: enabled && userId != null,
    staleTime: 60 * 1000,
    // A 403 or a 404 here is a decision about this person, not a blip — and
    // `/beskeder/ny` holds its composer back until this settles, so a retried
    // refusal is a screen that stays empty for longer, twice over.
    retry: retryUnlessRefused,
  });
}
